import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, CircleDashedIcon, XIcon } from 'lucide-react';
import type { ValidationStatus } from '../../types/dashboard';
import { metricDefinitions } from '../../data/definitions';
import { useDefinitions } from '../../contexts/DefinitionsContext';
import { SearchInput } from '../ui/TableControls';
import { ConversionWindowExample, MultiTerritoryExample } from '../funnel/CountingExamples';
import { DedupExample } from '../retention/DedupExample';
const STATUS_STYLE: Record<ValidationStatus, string> = {
  Confirmed: 'bg-positive-soft text-positive',
  Proposed: 'bg-navy-50 text-navy',
  'Pending validation': 'border border-dashed border-caution/40 bg-caution-soft text-caution'
};
const EXAMPLES: Record<string, () => React.ReactNode> = {
  members: () => <MultiTerritoryExample data-id="element-79" data-mp="39scmv8il0c61" />,
  conversion: () => <ConversionWindowExample data-id="element-80" data-mp="34qf0w6isr39e" />,
  regsPerMember: () => <ConversionWindowExample data-id="element-81" data-mp="34tt57kb0tmjh" />,
  seasonRetention: () => <DedupExample data-id="element-82" data-mp="3pgg3djhkkzpf" />,
  yoyRetention: () => <DedupExample data-id="element-83" data-mp="3pjkfd31a0zqn" />
};
export function DefinitionsDrawer() {
  const {
    isOpen,
    openId,
    close
  } = useDefinitions();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setExpanded(openId ?? metricDefinitions[0].id);
    const t = setTimeout(() => {
      if (openId) document.getElementById(`def-${openId}`)?.scrollIntoView({
        block: 'start'
      });
    }, 60);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, openId, close]);
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    return metricDefinitions.filter(d => !q || [d.name, d.definition, d.calculation, d.note ?? ''].some(s => s.toLowerCase().includes(q)));
  }, [query]);
  return <AnimatePresence data-id="element-84" data-mp="2jzbfxs7ewl1g">
      {isOpen && <div key="definitions" className="fixed inset-0 z-[60]" data-id="element-85" data-mp="2fzswl0v8oi0q">
          <motion.div className="absolute inset-0 bg-ink/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={close} data-id="element-86" data-mp="2fxgdulhnq3mm" />
          <motion.aside role="dialog" aria-modal="true" aria-label="Metric definitions" initial={{
        x: 32,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 32,
        opacity: 0
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute right-0 top-0 flex h-full w-full max-w-[560px] flex-col bg-white shadow-2xl" data-id="element-87" data-mp="2ose7xvwjjmfj">
            <header className="border-b border-line px-5 py-4" data-id="element-88" data-mp="3mfd0bug7a87m">
              <div className="flex items-start justify-between gap-3" data-id="element-89" data-mp="3mhpqguw43ovr">
                <div data-id="element-90" data-mp="3hfqkdd8zmdv4">
                  <h2 className="text-lg font-semibold text-navy" data-id="element-91" data-mp="3hga6l65o7oey">Metric definitions</h2>
                  <p className="text-xs text-ink-muted" data-id="element-92" data-mp="1377n1mqdjjjp">Proposed calculation logic for Adaptive scoping. Open assumptions are flagged.</p>
                </div>
                <button type="button" onClick={close} aria-label="Close definitions" className="rounded p-1 text-ink-muted hover:bg-canvas hover:text-ink" data-id="element-93" data-mp="u8m3ppd2w0hv">
                  <XIcon className="h-4 w-4" data-id="element-94" data-mp="z99vwed1mndo" />
                </button>
              </div>
              <div className="mt-3" data-id="element-95" data-mp="1mxjllrddnva3">
                <SearchInput value={query} onChange={setQuery} placeholder="Search definitions" data-id="element-96" data-mp="1qy3j4w1v50wr" />
              </div>
            </header>
            <ul className="flex-1 divide-y divide-line overflow-y-auto" data-id="element-97" data-mp="1d65gn42nq39k">
              {items.length === 0 && <li className="px-5 py-10 text-center text-xs text-ink-muted" data-id="element-98" data-mp="1i4qjlb9an11p">No definitions match.</li>}
              {items.map(d => {
            const isExpanded = expanded === d.id;
            const Example = EXAMPLES[d.id];
            return <li key={d.id} id={`def-${d.id}`} className={isExpanded ? 'bg-canvas/40' : ''} data-id="element-99" data-mp="m117c9smacv7">
                    <button type="button" aria-expanded={isExpanded} onClick={() => setExpanded(isExpanded ? null : d.id)} className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-canvas" data-id="element-100" data-mp="h0x1ddpc50j8">
                      <span className="flex-1" data-id="element-101" data-mp="14r97cj10sn76">
                        <span className="block text-[14px] font-semibold text-navy" data-id="element-102" data-mp="14t3qpswn8795">{d.name}</span>
                        {d.supporting && <span className="block text-[11px] text-ink-subtle" data-id="element-103" data-mp="vypey0bbneus">Supporting definition</span>}
                      </span>
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[d.status]}`} data-id="element-104" data-mp="r0e3jl5dowag">
                        {d.status === 'Pending validation' && <CircleDashedIcon className="h-3 w-3" aria-hidden data-id="element-105" data-mp="1jn8o0i273vnx" />}
                        {d.status}
                      </span>
                      <ChevronDownIcon className={`h-4 w-4 text-ink-subtle transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-106" data-mp="1r1izpb7wfy6m" />
                    </button>
                    {isExpanded && <div className="space-y-3 px-5 pb-5" data-id="element-107" data-mp="20zyq1bz3r3f2">
                        <p className="text-[13px] leading-relaxed text-ink" data-id="element-108" data-mp="24zz7z6letwqv">{d.definition}</p>
                        <div className="rounded-md border border-line bg-white px-3 py-2" data-id="element-109" data-mp="29xhhoz794fzx">
                          <div className="text-[11px] font-semibold text-ink-muted" data-id="element-110" data-mp="2ewbyjvlnruhe">Proposed calculation</div>
                          <p className="mt-0.5 font-mono text-[12px] leading-relaxed text-ink" data-id="element-111" data-mp="2jpew0etk3o2v">{d.calculation}</p>
                        </div>
                        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-xs" data-id="element-112" data-mp="2sos536drqan2">
                          <dt className="text-ink-muted" data-id="element-113" data-mp="2smxilnmumagv">Counting grain</dt>
                          <dd className="font-medium text-ink" data-id="element-114" data-mp="2xo4r5owtfdol">{d.grain}</dd>
                          <dt className="text-ink-muted" data-id="element-115" data-mp="xmeypgvmbxj5">Date basis</dt>
                          <dd className="font-medium text-ink" data-id="element-116" data-mp="tkjx0b9rzi4u">{d.dateBasis}</dd>
                          <dt className="text-ink-muted" data-id="element-117" data-mp="17bezn5tol2zz">Applies filters</dt>
                          <dd className="flex flex-wrap gap-1" data-id="element-118" data-mp="17di0kkwdggjh">
                            {d.filters.map(f => <span key={f} className="rounded border border-line bg-white px-1.5 py-0.5 text-[11px] text-ink-muted" data-id="element-119" data-mp="1h9ky1dga3r03">{f}</span>)}
                          </dd>
                        </dl>
                        {d.note && <p className="rounded-md bg-caution-soft px-3 py-2 text-xs text-caution" data-id="element-120" data-mp="1q9hsxwt9hm3l">
                            <b data-id="element-121" data-mp="1lbzfpu87okps">Open assumption:</b> {d.note}
                          </p>}
                        {Example && <div className="rounded-md border border-line bg-white p-3.5" data-id="element-122" data-mp="3n3cmkotqp2oh">
                            <div className="mb-2.5 text-[11px] font-semibold text-ink-muted" data-id="element-123" data-mp="22k0g9tdyezop">Worked example (sample)</div>
                            {Example()}
                          </div>}
                      </div>}
                  </li>;
          })}
            </ul>
          </motion.aside>
        </div>}
    </AnimatePresence>;
}